import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Field, FieldGrid, SectionCard } from '../../../components/Section';
import { TREASURY_KYB_DOCUMENTS, TREASURY_VERIFICATION_CHECKS } from '../../../constants/treasury';
import { usePermissions } from '../../../hooks';
import {
  useFetchTreasuryKybReadinessQuery,
  useVerifyTreasuryKybMutation,
} from '../../../redux/features/treasury/treasury.apiSlice';
import { locations } from '../../../routes/locations';
import { TreasuryKyb, TreasuryKybReadiness } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../utils/treasury';

type TreasuryAdminVerificationStepProps = {
  companyId: number;
  /** The saved confirmation, if any — the ticks start from it and confirming again replaces it. */
  verification?: TreasuryKyb['verification'];
  onDone: () => void;
};

/**
 * Step 6 — "company verified". The Madkhol admin checks the company by hand (contract, documents with the government)
 * and confirms here; the six company checks then go to Alpaca as this admin's manual review. The owner's ID-document
 * tick is required (Alpaca refuses the owner without it); the photo tick is sent only when ticked. Saving the KYB
 * again clears this confirmation.
 */
export const TreasuryAdminVerificationStep = ({
  companyId,
  verification = null,
  onDone,
}: TreasuryAdminVerificationStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canVerify = usePermissions({ items: [permissions.Treasury.updateKyb] });
  const [ownerDocumentChecked, setOwnerDocumentChecked] = useState(Boolean(verification?.ownerDocumentChecked));
  const [ownerPhotoMatched, setOwnerPhotoMatched] = useState(Boolean(verification?.ownerPhotoMatched));
  const {
    data: readinessData,
    isFetching: isReadinessLoading,
    isError: isReadinessError,
    error: readinessError,
    refetch: loadReadiness,
  } = useFetchTreasuryKybReadinessQuery(companyId);
  // A failed read shows every document as missing, as before (never "uploaded" on a guess).
  const readiness: TreasuryKybReadiness | null = isReadinessError ? null : readinessData ?? null;
  const [verifyTreasuryKyb, { isLoading: isVerifying }] = useVerifyTreasuryKybMutation();

  useEffect(() => {
    if (isReadinessError) {
      enqueueSnackbar(getTreasuryErrorMessage(readinessError, 'Failed to load the documents'), { variant: 'error' });
    }
  }, [isReadinessError, readinessError, enqueueSnackbar]);

  const verify = async () => {
    try {
      await verifyTreasuryKyb({ companyId, payload: { ownerDocumentChecked, ownerPhotoMatched } }).unwrap();
      enqueueSnackbar('Company verified!', { variant: 'success' });
      onDone();
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Company verification error!'), { variant: 'error' });
    }
  };

  const missingDocuments = TREASURY_KYB_DOCUMENTS.filter(
    (document) => readiness?.missing.includes(document.missingCode),
  );
  const uploadedCount = TREASURY_KYB_DOCUMENTS.length - missingDocuments.length;

  return (
    <Stack spacing={2}>
      {verification && (
        <SectionCard
          title="Current Confirmation"
          description="Confirming again replaces it with your name, the time now and the ticks below."
          action={<Chip size="small" color="success" label="Verified" />}
        >
          <FieldGrid columns={2}>
            <Field label="Verified By" value={verification.verifiedByName} />
            <Field label="Verified At" value={new Date(verification.verifiedAt).toLocaleString()} />
          </FieldGrid>
        </SectionCard>
      )}

      <SectionCard
        title="Company Documents"
        description="The Alpaca account opens only when all four are uploaded."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {!isReadinessLoading && (
              <Chip
                size="small"
                color={missingDocuments.length ? 'warning' : 'success'}
                label={`${uploadedCount} / ${TREASURY_KYB_DOCUMENTS.length} uploaded`}
              />
            )}
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadReadiness}>
              Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileUploadIcon />}
              href={locations.uploadCompanyDocument(companyId)}
              target="_blank"
            >
              Upload
            </Button>
          </Stack>
        }
      >
        {isReadinessLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={1}>
            {TREASURY_KYB_DOCUMENTS.map((document) => {
              const isMissing = !readiness || readiness.missing.includes(document.missingCode);

              return (
                <Grid item xs={12} md={6} key={document.type}>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {isMissing ? (
                        <ErrorOutlineIcon color="error" fontSize="small" />
                      ) : (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={document.label}
                      secondary={isMissing ? `Missing — upload as type "${document.type}"` : 'Uploaded'}
                    />
                  </ListItem>
                </Grid>
              );
            })}
          </Grid>
        )}
      </SectionCard>

      <SectionCard
        title="Company Checks"
        description="What you confirm you checked by hand — sent to Alpaca as your manual review."
      >
        <List dense disablePadding sx={{ columns: { xs: 1, md: 2 } }}>
          {TREASURY_VERIFICATION_CHECKS.map((check) => (
            <ListItem key={check} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FactCheckIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={check} />
            </ListItem>
          ))}
        </List>
      </SectionCard>

      <SectionCard title="Owner" description="The two identity checks on the owner.">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  id="ownerDocumentChecked"
                  name="ownerDocumentChecked"
                  checked={ownerDocumentChecked}
                  onChange={(event) => setOwnerDocumentChecked(event.target.checked)}
                />
              }
              label={<Typography variant="body2">Owner&apos;s ID document checked *</Typography>}
            />
            <FormHelperText>Required — Alpaca refuses the owner without the document check.</FormHelperText>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  id="ownerPhotoMatched"
                  name="ownerPhotoMatched"
                  checked={ownerPhotoMatched}
                  onChange={(event) => setOwnerPhotoMatched(event.target.checked)}
                />
              }
              label={<Typography variant="body2">Owner&apos;s photo matched to the ID</Typography>}
            />
            <FormHelperText>Optional — sent to Alpaca only when ticked.</FormHelperText>
          </Grid>
        </Grid>
      </SectionCard>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isVerifying}
          startIcon={<FactCheckIcon />}
          onClick={verify}
          disabled={!ownerDocumentChecked || isVerifying || !canVerify}
        >
          {verification ? 'Confirm Again' : 'Confirm Company Verified'}
        </LoadingButton>
      </Box>
    </Stack>
  );
};
